import React from 'react';
import { Switch as ArkSwitch } from '@ark-ui/react/switch';
import clsx from 'clsx';
import styles from './Switch.module.scss';

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Popisek vedle přepínače; bez ARIA vazby slouží i jako přístupné jméno. */
  label?: string;
  disabled?: boolean;
  readOnly?: boolean;
  /** Chybový stav — vizuálně zvýrazní ovládací prvek. */
  invalid?: boolean;
  onBlur?: () => void;
  className?: string;
  'data-testid'?: string;
  /**
   * Props předané na skrytý `input` — jméno pole, `ref`, ARIA vazby a QA
   * atributy. Používá {@link SwitchInput}, který přepíná napojuje na
   * `react-hook-form`.
   */
  hiddenInputProps?: React.ComponentProps<typeof ArkSwitch.HiddenInput>;
  /** Přepis interních id (Ark) kvůli deterministickým ARIA vazbám. */
  ids?: React.ComponentProps<typeof ArkSwitch.Root>['ids'];
}

/**
 * Přepínač (toggle) postavený na Ark UI `Switch` — jediná implementace vzhledu
 * přepínače v knihovně.
 *
 * Sám o sobě je řízený prostým `checked`/`onCheckedChange`, takže se dá použít
 * i mimo formulář (např. filtr nad seznamem). Ve formuláři se používá
 * `SwitchInput` z forms modulu, který je tenkou nadstavbou nad touto
 * komponentou a doplňuje napojení na `react-hook-form`.
 */
export const Switch: React.FC<SwitchProps> = ({
  checked,
  onCheckedChange,
  label,
  disabled,
  readOnly,
  invalid,
  onBlur,
  className,
  ids,
  hiddenInputProps,
  'data-testid': dataTestId,
}) => (
  <ArkSwitch.Root
    checked={checked}
    disabled={disabled}
    readOnly={readOnly}
    invalid={invalid}
    onCheckedChange={(details) => onCheckedChange(details.checked)}
    onBlur={onBlur}
    className={clsx(styles.switchRoot, className)}
    data-selected={checked ? 'true' : 'false'}
    ids={ids}
  >
    <ArkSwitch.Control className={styles.switchControl}>
      <ArkSwitch.Thumb className={styles.switchThumb} />
    </ArkSwitch.Control>
    {label && (
      <ArkSwitch.Label className={styles.switchLabel}>{label}</ArkSwitch.Label>
    )}
    <ArkSwitch.HiddenInput data-testid={dataTestId} {...hiddenInputProps} />
  </ArkSwitch.Root>
);

Switch.displayName = 'Switch';
